import React from "react";

import { useTranslation } from "react-i18next";
import { FrontendOrder, FrontendUserInfo } from "node-library/dist/portal";

import { OrderStatus, Severity, SystemRole } from "node-library/dist/constants";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";

import { NeutralColor } from "node-library/dist/colors";

import { Card, Tooltip } from "@mui/material";
import { useSnackbar } from "../../../../../common-components/toast/showToastMessage";
import {
	getStatusColors,
	getStatusDescriptionText,
	getStatusString,
} from "../../../../../helpers/orderHelper";
import {
	AlertIcon,
	COAIcon,
	DeleteIcon,
	EditIcon,
	UploadIcon,
	ViewResultIcon,
} from "../../../../../common-components/svgIcons";
import { KebabDropdownButton } from "../../../../../common-components/button";

interface Props {
	order: FrontendOrder;
	user: FrontendUserInfo | undefined;
	goToViewOrderDetails: () => void;
	viewOrderCOC: () => void;

	deleteOrder: () => void;
	// editOrder: () => void;
}

export default function AdminOrderCell(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { order, goToViewOrderDetails, user, viewOrderCOC, deleteOrder } =
		props;

	const statusString: string = getStatusDescriptionText(
		order.orderStatus,
		translation
	);
	const statusColor: {
		backgroundColor: string;
		borderColor: string;
		textColor: string;
	} = getStatusColors(order.orderStatus);

	let actionOptions: Array<{
		key: string;
		onClick: () => void;
		children: React.ReactNode;
	}>;

	actionOptions = [
		{
			key: `view-coc-order-${order.orderBarcodeId}`,
			onClick: () => {
				viewOrderCOC();
			},
			children: (
				<div className="icon-dropdown-menu-item">
					<COAIcon />
					{translation("ViewChainOfCustody")}
				</div>
			),
		},
		{
			key: `delete-order-${order.orderBarcodeId}`,
			onClick: () => {
				deleteOrder();
			},
			children: (
				<div className="icon-dropdown-menu-item">
					<DeleteIcon />
					{translation("Delete")}
				</div>
			),
		},
	];

	const orderHeaderRow: React.ReactNode = (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: "5px",
			}}
		>
			<div
				className="typography-caption sample-status-box"
				style={{
					backgroundColor: statusColor.backgroundColor,
					border: `1px solid ${statusColor.borderColor}`,
					color: statusColor.textColor,
				}}
			>
				{statusString}
			</div>

			<KebabDropdownButton
				menuOptions={actionOptions}
				id={`batch-more-action-menu-${order.orderBarcodeId}`}
				onClick={(e) => {}}
			></KebabDropdownButton>
		</div>
	);

	let licenseInfo: React.ReactNode;

	licenseInfo = order.licenseNumber || translation("None");

	const orderBody: React.ReactNode = (
		<div
			style={{
				marginTop: "10px",
				display: "flex",
				flexDirection: "column",
				gap: "5px",
			}}
		>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("OrderNumberShort")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{order.orderBarcodeId}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("License")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{licenseInfo}
				</div>
			</div>
			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Samples")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{order.sampleCount}
				</div>
			</div>

			<div className="order-cell-body-row typography-body-02">
				<div className="sample-info-row-description">
					{translation("Receivedon")}
				</div>
				<div className="overflow-ellipsis-text" style={{ maxWidth: "50%" }}>
					{order.inserted ? formatCustomDate(order.inserted) : "--"}
				</div>
			</div>
		</div>
	);

	return (
		<Card
			className="card-border-radius-14p card-medium-padding highlight-card"
			style={{ cursor: "pointer" }}
			key={order.orderBarcodeId}
			onClick={() => {
				goToViewOrderDetails();
			}}
		>
			{orderHeaderRow}
			{orderBody}
		</Card>
	);
}
